import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertTriangle,
  Banknote,
  CheckCheck,
  CircleCheck,
  Clock,
  FileText,
  Mail,
  Package,
  Phone,
  Receipt,
  SquareCheck,
  Store,
  Truck,
  User,
  type LucideIcon,
} from 'lucide-react'
import { Spinner } from '../../components/ui/Spinner'
import {
  citeste,
  scrie,
  useDeRezolvat,
  useScadente,
  ziLocala,
  type Grup,
  type Iconita,
  type Rand,
  type Ton,
} from './date'

const ICON: Record<Iconita, LucideIcon> = {
  aproba: CheckCheck,
  cutie: Package,
  bani: Banknote,
  camion: Truck,
  task: SquareCheck,
  tel: Phone,
  doc: FileText,
  alerta: AlertTriangle,
  ceas: Clock,
  mail: Mail,
  bon: Receipt,
  om: User,
  stand: Store,
}

const GRUPURI: { id: Grup; nume: string }[] = [
  { id: 'intarziate', nume: 'Întârziate' },
  { id: 'azi', nume: 'Azi' },
  { id: 'saptamana', nume: 'Săptămâna asta' },
]

/** Culoarea e semnal: rosu — intarziat sau blocheaza; galben — de facut azi; restul gri. */
const TON_ICON: Record<Ton, string> = {
  rosu: 'bg-b4l-red-light text-b4l-red-dark',
  galben: 'bg-b4l-amber-light text-b4l-amber-dark',
  neutru: 'bg-b4l-bg text-b4l-text-muted',
}

/** Cat se vede din prima: sapte randuri, apoi „Arată toate". */
const PRIMELE = 7

export function DeRezolvat({ utilizator }: { utilizator: number | undefined }) {
  const rez = useDeRezolvat()
  const sc = useScadente()
  const [toate, setToate] = useState(false)

  // „Mâine": randul dispare azi si revine maine. Doar pentru ce nu e rosu — ce e intarziat nu se amana.
  const cheie = `b4l.acasa.amanate.${utilizator ?? 0}`
  const [amanate, setAmanate] = useState<Record<string, string>>(() => citeste(cheie, {}))
  const azi = ziLocala()
  const amana = (id: string) => {
    const curate = Object.fromEntries(Object.entries(amanate).filter(([, z]) => z === azi))
    const noi = { ...curate, [id]: azi }
    scrie(cheie, noi)
    setAmanate(noi)
  }

  const randuri = useMemo(() => {
    const ordine: Record<Grup, number> = { solicitari: 0, intarziate: 1, azi: 2, saptamana: 3 }
    const ton: Record<Ton, number> = { rosu: 0, galben: 1, neutru: 2 }
    return (
      [...(sc.data?.randuri ?? []), ...(rez.data?.randuri ?? [])]
        // Solicitarile stau sus, in banda lor (`BandaSolicitari`) — nu si aici, a doua oara.
        .filter((r) => r.grup !== 'solicitari')
        .filter((r) => amanate[r.id] !== azi)
        .sort((a, b) => ordine[a.grup] - ordine[b.grup] || ton[a.ton] - ton[b.ton])
    )
  }, [rez.data, sc.data, amanate, azi])

  // Solicitarile sunt sus, in banda lor: „nimic nu asteapta dupa tine" ar fi neadevarat.
  const areSolicitari = (rez.data?.randuri ?? []).some((r) => r.grup === 'solicitari')
  const neverificate = [...(rez.data?.neverificate ?? []), ...(sc.data?.neverificate ?? [])]
  if (rez.isError) neverificate.push('lista de lucru')
  if (sc.isError) neverificate.push('scadențele clienților')

  if (rez.isLoading)
    return (
      <div className="flex justify-center py-8">
        <Spinner label="Se adună ce e de rezolvat…" />
      </div>
    )

  const vizibile = toate ? randuri : randuri.slice(0, PRIMELE)
  const amanateAzi = Object.values(amanate).filter((z) => z === azi).length

  return (
    <div className="grid gap-3">
      {neverificate.length > 0 && (
        <p
          role="status"
          className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md bg-b4l-amber-light px-3 py-2 text-mic text-b4l-amber-dark"
        >
          <AlertTriangle size={14} aria-hidden="true" />
          N-am putut verifica acum: {neverificate.join(', ')}.
          <button
            type="button"
            onClick={() => {
              void rez.refetch()
              void sc.refetch()
            }}
            className="font-semibold underline"
          >
            Reîncearcă
          </button>
        </p>
      )}

      {randuri.length === 0
        ? neverificate.length === 0 && (
            <p className="flex items-center gap-2 py-4 text-corp text-b4l-text-muted">
              <CircleCheck size={18} className="text-b4l-green" aria-hidden="true" />
              {areSolicitari
                ? 'În rest, totul la zi.'
                : 'Totul la zi — nimic nu așteaptă după tine.'}
              {sc.isLoading && (
                <span className="text-b4l-text-dim"> (încă verific scadențele…)</span>
              )}
            </p>
          )
        : GRUPURI.map((g) => {
            const ale = vizibile.filter((r) => r.grup === g.id)
            if (ale.length === 0) return null
            return (
              <div key={g.id} className="grid gap-1">
                <h3
                  className={`text-eticheta uppercase ${g.id === 'intarziate' ? 'text-b4l-red' : 'text-b4l-text-dim'}`}
                >
                  {g.nume}
                </h3>
                <ul className="grid divide-y divide-b4l-border">
                  {ale.map((r) => (
                    <RandRezolvat key={r.id} r={r} laMaine={() => amana(r.id)} />
                  ))}
                </ul>
              </div>
            )
          })}

      {(randuri.length > PRIMELE || amanateAzi > 0 || sc.isLoading) && (
        <div className="flex flex-wrap items-center justify-between gap-2 text-mic text-b4l-text-dim">
          <span>
            {sc.isLoading && 'Încă verific scadențele clienților… '}
            {amanateAzi > 0 &&
              `${amanateAzi === 1 ? 'Un rând amânat' : `${amanateAzi} rânduri amânate`} pe mâine.`}
          </span>
          {randuri.length > PRIMELE && (
            <button
              type="button"
              onClick={() => setToate((v) => !v)}
              className="font-semibold text-b4l-blue hover:underline"
            >
              {toate ? 'Arată mai puțin' : `Arată toate (${randuri.length})`}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

function RandRezolvat({ r, laMaine }: { r: Rand; laMaine: () => void }) {
  const Icon = ICON[r.icon] ?? AlertTriangle
  return (
    <li className="grid grid-cols-[32px_minmax(0,1fr)] items-center gap-x-3 gap-y-2 py-2.5 sm:grid-cols-[32px_minmax(0,1fr)_auto]">
      <span
        className={`flex h-8 w-8 items-center justify-center rounded-full ${TON_ICON[r.ton]}`}
        aria-hidden="true"
      >
        <Icon size={16} />
      </span>
      <span className="grid min-w-0 gap-0.5">
        <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-corp font-medium text-b4l-text">
          {r.titlu}
          {r.semnal && (
            <span
              className={`rounded-full px-1.5 py-px text-eticheta ${r.ton === 'rosu' ? 'bg-b4l-red-light text-b4l-red-dark' : 'bg-b4l-bg text-b4l-text-muted'}`}
            >
              {r.semnal}
            </span>
          )}
        </span>
        {r.detaliu && <span className="text-mic text-b4l-text-muted">{r.detaliu}</span>}
      </span>
      {/* Pe telefon butonul trece pe randul lui, pe toata latimea — la indemana degetului. */}
      <span className="col-span-2 flex items-center justify-end gap-1.5 sm:col-span-1">
        {r.ton !== 'rosu' && (
          <button
            type="button"
            onClick={laMaine}
            className="rounded-md px-2 py-1.5 text-mic text-b4l-text-dim hover:bg-b4l-bg hover:text-b4l-text"
            aria-label={`Amână pe mâine: ${r.titlu}`}
          >
            Mâine
          </button>
        )}
        <Link
          to={r.link}
          className={`inline-flex min-h-[36px] flex-1 items-center justify-center rounded-md px-3 text-corp font-semibold sm:flex-none ${
            r.ton === 'neutru'
              ? 'border border-b4l-border text-b4l-text hover:bg-b4l-bg'
              : 'bg-b4l-blue text-b4l-pe-accent hover:opacity-90'
          }`}
        >
          {r.buton}
        </Link>
      </span>
    </li>
  )
}

/**
 * Banda rosie din capul paginii, sub salut (Madalin, 19.10.2026: solicitarile „sa apara si pe zona
 * principala, undeva in partea de sus, tot cu rosu"). Doar cand cineva asteapta raspunsul tau;
 * altfel nu exista. Fiecare cu butonul ei („Verifică", „Corectează"); in „De rezolvat" nu se mai
 * repeta.
 */
export function BandaSolicitari() {
  const rez = useDeRezolvat()
  const solicitari = (rez.data?.randuri ?? []).filter((r) => r.grup === 'solicitari')
  if (solicitari.length === 0) return null
  return (
    <section
      aria-labelledby="solicitari-noi"
      className="grid gap-1 rounded-caseta border border-b4l-red/40 border-l-4 border-l-b4l-red bg-b4l-red-light px-4 py-3"
    >
      <h2
        id="solicitari-noi"
        className="flex items-center gap-2 text-eticheta font-semibold uppercase tracking-wide text-b4l-red-dark"
      >
        <span className="relative flex h-2.5 w-2.5" aria-hidden="true">
          <span className="absolute inline-flex h-full w-full rounded-full bg-b4l-red opacity-60 motion-safe:animate-ping" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-b4l-red" />
        </span>
        {solicitari.length === 1
          ? 'Solicitare nouă · așteaptă răspunsul tău'
          : `${solicitari.length} solicitări noi · așteaptă răspunsul tău`}
      </h2>
      <ul className="grid divide-y divide-b4l-red/20">
        {solicitari.map((r) => {
          const Icon = ICON[r.icon] ?? AlertTriangle
          return (
            <li
              key={r.id}
              className="grid grid-cols-[32px_minmax(0,1fr)] items-center gap-x-3 gap-y-2 py-2.5 sm:grid-cols-[32px_minmax(0,1fr)_auto]"
            >
              <span
                className="flex h-8 w-8 items-center justify-center rounded-full bg-b4l-red text-b4l-pe-accent"
                aria-hidden="true"
              >
                <Icon size={16} />
              </span>
              <span className="grid min-w-0 gap-0.5">
                <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-corp font-semibold text-b4l-red-dark">
                  {r.titlu}
                  {r.semnal && (
                    <span className="rounded-full bg-b4l-red px-1.5 py-px text-eticheta font-semibold text-b4l-pe-accent">
                      {r.semnal}
                    </span>
                  )}
                </span>
                {r.detaliu && <span className="text-mic text-b4l-red-dark/80">{r.detaliu}</span>}
              </span>
              {/* Butonul lui: „Verifică" la aprobari, „Corectează" la o cerere trimisa inapoi. */}
              <Link
                to={r.link}
                className="col-span-2 inline-flex min-h-[36px] items-center justify-center rounded-md bg-b4l-red px-3 text-corp font-semibold text-b4l-pe-accent hover:opacity-90 sm:col-span-1"
              >
                {r.buton}
              </Link>
            </li>
          )
        })}
      </ul>
    </section>
  )
}
